import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Download } from 'lucide-react';
import { portfolioData } from '../data/mock';

const SCROLL_THRESHOLD = 20;

const NAV_ITEMS = [
  { id: 'work', label: 'Work' },
  { id: 'about', label: 'About' },
  { id: 'expertise', label: 'Expertise' },
  { id: 'contact', label: 'Contact' },
];

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  const handleScroll = useCallback(() => setIsScrolled(window.scrollY > SCROLL_THRESHOLD), []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
    return () => { document.body.style.overflow = 'unset'; };
  }, [isMobileMenuOpen]);

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false);
    if (location.pathname !== '/') {
      window.location.href = `/#${sectionId}`;
      return;
    }
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled ? 'glass-nav' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-20">
            <Link
              to="/"
              data-testid="logo-link"
              className="text-3xl font-logo silver-text leading-none"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              LH
            </Link>

            <nav className="hidden md:flex items-center gap-10">
              {NAV_ITEMS.map((item) => (
                <button
                  key={item.id}
                  data-testid={`nav-${item.id}`}
                  onClick={() => scrollToSection(item.id)}
                  className="text-sm font-medium text-zinc-400 hover:text-white transition-colors duration-300"
                >
                  {item.label}
                </button>
              ))}
              <a
                href={portfolioData.personal.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-resume-link"
                className="glass-btn inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white"
              >
                <Download size={15} />
                Resume
              </a>
            </nav>

            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden z-50 relative text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div
        data-testid="mobile-menu-drawer"
        className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] glass z-40 md:hidden transform transition-transform duration-300 ease-in-out ${
          isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <nav className="flex flex-col h-full pt-24 px-6 pb-8">
          <div className="space-y-1 flex-1">
            {NAV_ITEMS.map((item) => (
              <button
                key={item.id}
                data-testid={`mobile-nav-${item.id}`}
                onClick={() => scrollToSection(item.id)}
                className="block w-full text-left py-4 text-lg font-medium text-zinc-300 hover:text-white transition-colors border-b border-white/5"
              >
                {item.label}
              </button>
            ))}
          </div>
          <a
            href={portfolioData.personal.resumeUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-resume-link"
            className="glass-btn flex items-center justify-center gap-2 w-full mt-6 py-3.5 rounded-full text-base font-medium text-white"
          >
            <Download size={17} />
            Download Resume
          </a>
        </nav>
      </div>
    </>
  );
};

export default Header;
